import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@material-ui/core'
import React, { useState } from 'react'

import {
  ticketResolutionLabel,
  TicketResolutionCode,
  TICKET_RESOLUTION_CODES
} from '../../helpers/ticketWorkflow'
import { getTranslate } from '../../localization'

const NOTES_MAX = 2000

/**
 * Why this ticket is being resolved.
 *
 * ticket-service will not resolve a ticket without a code — it answers
 * "resolving a ticket requires a resolution code" — so this is not an optional
 * nicety bolted onto the button. Until the BFF began forwarding the field, the
 * Resolve button in the action bar could only ever fail, on every ticket.
 *
 * A dialog rather than an inline expansion of the action bar. Resolving is the
 * one transition that asks a question, and the others must stay single-click;
 * folding a question into a row of verbs would slow down the three that have
 * nothing to ask.
 *
 * Radios rather than a select, matching the comment composer's scope control:
 * eight options is few enough to read at once, and the reason a ticket closed
 * is worth choosing deliberately rather than accepting whatever was in front.
 * No default is preselected for the same reason — a preselected code would be
 * recorded by anyone who pressed straight through.
 */
export default function ResolveDialog({
  open,
  busy,
  onCancel,
  onConfirm
}: {
  open: boolean
  busy: boolean
  onCancel: () => void
  onConfirm: (code: TicketResolutionCode, notes: string) => void
}) {
  const [code, setCode] = useState<TicketResolutionCode | ''>('')
  const [notes, setNotes] = useState('')

  function close() {
    setCode('')
    setNotes('')
    onCancel()
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : close}
      aria-labelledby="resolve-dialog-title"
      maxWidth="xs"
      fullWidth>
      <DialogTitle id="resolve-dialog-title">
        {getTranslate('Resolve this ticket')}
      </DialogTitle>
      <DialogContent>
        <FormControl component="fieldset" style={{ display: 'block' }}>
          <FormLabel component="legend">{getTranslate('Resolution')}</FormLabel>
          <RadioGroup
            name="resolutionCode"
            value={code}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setCode(event.target.value as TicketResolutionCode)
            }>
            {TICKET_RESOLUTION_CODES.map((value) => (
              <FormControlLabel
                key={value}
                value={value}
                control={<Radio size="small" color="primary" />}
                label={ticketResolutionLabel(value)}
              />
            ))}
          </RadioGroup>
        </FormControl>

        {/* Optional, and bounded to the same limit the service enforces, so an
            over-long note is refused here rather than after a round trip. */}
        <TextField
          id="resolutionNotes"
          label={getTranslate('Notes (optional)')}
          multiline
          rows={3}
          fullWidth
          margin="dense"
          value={notes}
          inputProps={{ maxLength: NOTES_MAX }}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            setNotes(event.target.value)
          }
        />
        <Typography variant="caption" color="textSecondary" component="p">
          {getTranslate(
            'The resolution is recorded on the ticket and cannot be edited afterwards.'
          )}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={close} disabled={busy}>
          {getTranslate('Cancel')}
        </Button>
        {/* aria-disabled, not disabled: the native attribute drops the button
            out of the tab order, so a keyboard user who reaches it before
            choosing a code loses their place. The guard is the click handler. */}
        <Button
          size="small"
          variant="contained"
          color="primary"
          aria-disabled={!code || busy}
          onClick={() => {
            if (!code || busy) return
            onConfirm(code, notes.trim())
          }}>
          {getTranslate('Resolve')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
